import Translations from "@translate/components/ChatOnboarding/parseRequestPortfolioResult";
import { I18nUtil } from "@utils/i18n";
import { ACCOUNT_CATEGORY_CARD_COPY } from "@components/PlaidConnectFlow/shared/AccountCategoryCards";
import { LEGACY_REQUEST_PORTFOLIO_ACTION } from "./requestPortfolioAction";
import type { RequestPortfolioArgsLike } from "./resolveRequestPortfolioLabels";
import { resolveRequestPortfolioLabels } from "./resolveRequestPortfolioLabels";

export type { RequestPortfolioArgsLike };

const I18n = I18nUtil.createScoped(Translations);

type RequestPortfolioResultAction =
  | "connect_investment"
  | "connect_bank"
  | (typeof LEGACY_REQUEST_PORTFOLIO_ACTION)[keyof typeof LEGACY_REQUEST_PORTFOLIO_ACTION];

export interface RequestPortfolioResultDisplay {
  action: RequestPortfolioResultAction;
  title: string;
  body: string;
}

/** 历史消息查看态：旧版交互态各 action 对应的选项文案（translate key 不变） */
const resolveLegacyActionLabel = (action: string): string => {
  switch (action) {
    case LEGACY_REQUEST_PORTFOLIO_ACTION.SCREENSHOT_RECOGNIZED:
      return I18n.t("Snap a photo");
    case LEGACY_REQUEST_PORTFOLIO_ACTION.PDF_RECOGNIZED:
      return I18n.t("Upload a statement");
    case LEGACY_REQUEST_PORTFOLIO_ACTION.SKIPPED:
      return I18n.t("Maybe later");
    case LEGACY_REQUEST_PORTFOLIO_ACTION.CONNECT_PLAID:
      return I18n.t("Connect portfolio");
    default:
      return "";
  }
};

/** 新版交互态 action 与 AccountCategoryCards 卡片标题共用同一文案来源 */
const resolveRequestPortfolioActionLabel = (action: any): string => {
  switch (action) {
    case "connect_investment":
      return ACCOUNT_CATEGORY_CARD_COPY.invest.title;
    case "connect_bank":
      return ACCOUNT_CATEGORY_CARD_COPY.bank.title;
    default:
      return resolveLegacyActionLabel(String(action));
  }
};

/**
 * 解析 request_portfolio 的 result / role:tool content。
 * 支持 JSON 字符串或对象；action 映射为查看态正文文案。
 */
export const parseRequestPortfolioResult = (
  result: unknown,
  args?: RequestPortfolioArgsLike,
): RequestPortfolioResultDisplay | undefined => {
  if (result == null || result === "") {
    return undefined;
  }

  let parsed: unknown = result;
  if (typeof result === "string") {
    try {
      parsed = JSON.parse(result);
    } catch {
      return undefined;
    }
  }

  if (!parsed || typeof parsed !== "object") {
    return undefined;
  }

  const candidate = parsed as Record<string, unknown>;

  const action: any = candidate.action;
  const labels = resolveRequestPortfolioLabels(args);
  const fromResult =
    typeof candidate.title === "string" ? candidate.title.trim() : "";
  const title = fromResult || labels.title;
  const body =
    resolveRequestPortfolioActionLabel(action) ||
    (candidate?.content as any)?.trim() ||
    "";

  return { action, title, body };
};

export const hasRequestPortfolioResult = (result: unknown): boolean =>
  parseRequestPortfolioResult(result) != null;
